/**
 * Cascade Hooks
 * Manages cascade configuration for admin panel
 *
 * NOTE: Cascade V2 uses hash-based identification for providers.
 * Each provider instance has a unique hash (e.g., "5u3xk7")
 */

import { useApi, useMutation } from './useApi';
import { adminAPI } from '@/services/api';
import type {
  CascadeConfigResponse,
  DisabledMethodsResponse,
  CascadeStatsResponse,
} from '@/types';

// =============================================================================
// Deposit Cascade Hook
// =============================================================================

export interface UseCascadeOptions {
  fetchOnMount?: boolean;
  onSuccess?: (data: CascadeConfigResponse) => void;
  onError?: (error: string) => void;
}

/**
 * Hook for managing deposit cascade configuration
 *
 * @example
 * const {
 *   positions, enabled, isLoading,
 *   toggleCascade, swapPositions, dropMethod, addMethod,
 *   refetch
 * } = useDepositCascade({ fetchOnMount: true });
 */
export function useDepositCascade(options: UseCascadeOptions = {}) {
  const { fetchOnMount = false, onSuccess, onError } = options;

  // Main config
  const configApi = useApi<CascadeConfigResponse>(
    () => adminAPI.getDepositCascade(),
    { fetchOnMount, onSuccess, onError }
  );

  // Disabled methods
  const disabledApi = useApi<DisabledMethodsResponse>(
    () => adminAPI.getDisabledDepositMethods()
  );

  // Mutations - using hash-based identification
  const toggleMutation = useMutation<{ message: string }, [boolean]>(
    (enabled) => adminAPI.toggleDepositCascade(enabled)
  );

  const swapMutation = useMutation<{ message: string }, [string, string]>(
    (hash1, hash2) => adminAPI.swapDepositPositions({ hash1, hash2 })
  );

  const dropMutation = useMutation<{ message: string }, [string]>(
    (hash) => adminAPI.dropFromDepositCascade({ hash })
  );

  const addMutation = useMutation<{ message: string }, [string, number]>(
    (hash, position) => adminAPI.addToDepositCascade({ hash, position })
  );

  const reorderMutation = useMutation<{ message: string }, [string[]]>(
    (order) => adminAPI.reorderDepositCascade({ order })
  );

  return {
    // Data
    positions: configApi.data?.positions ?? [],
    enabled: configApi.data?.enabled ?? false,
    disabledMethods: disabledApi.data?.methods ?? [],

    // Loading states
    isLoading: configApi.isLoading,
    isLoadingDisabled: disabledApi.isLoading,
    error: configApi.error,

    // Actions
    refetch: configApi.execute,
    fetchDisabledMethods: disabledApi.execute,

    toggleCascade: toggleMutation.mutate,
    isToggling: toggleMutation.isLoading,

    // Hash-based operations
    swapPositions: swapMutation.mutate,
    isSwapping: swapMutation.isLoading,

    dropMethod: dropMutation.mutate,
    isDropping: dropMutation.isLoading,

    addMethod: addMutation.mutate,
    isAdding: addMutation.isLoading,

    reorderCascade: reorderMutation.mutate,
    isReordering: reorderMutation.isLoading,

    isMutating:
      toggleMutation.isLoading ||
      swapMutation.isLoading ||
      dropMutation.isLoading ||
      addMutation.isLoading ||
      reorderMutation.isLoading,
  };
}

// =============================================================================
// Withdrawal Cascade Hook
// =============================================================================

/**
 * Hook for managing withdrawal cascade configuration
 *
 * @example
 * const { positions, isLoading, swapPositions, dropMethod, addMethod } = useWithdrawalCascade({ fetchOnMount: true });
 */
export function useWithdrawalCascade(options: UseCascadeOptions = {}) {
  const { fetchOnMount = false, onSuccess, onError } = options;

  // Main config
  const configApi = useApi<CascadeConfigResponse>(
    () => adminAPI.getWithdrawalCascade(),
    { fetchOnMount, onSuccess, onError }
  );

  // Disabled methods
  const disabledApi = useApi<DisabledMethodsResponse>(
    () => adminAPI.getDisabledWithdrawalMethods()
  );

  // Mutations - using hash-based identification
  // NOTE: toggleWithdrawalCascade was removed - there is no separate withdrawal cascade toggle.
  // The deposit cascade toggle (toggleDepositCascade) toggles the ENTIRE cascade.

  const swapMutation = useMutation<{ message: string }, [string, string]>(
    (hash1, hash2) => adminAPI.swapWithdrawalPositions({ hash1, hash2 })
  );

  const dropMutation = useMutation<{ message: string }, [string]>(
    (hash) => adminAPI.dropFromWithdrawalCascade({ hash })
  );

  const addMutation = useMutation<{ message: string }, [string, number]>(
    (hash, position) => adminAPI.addToWithdrawalCascade({ hash, position })
  );

  const reorderMutation = useMutation<{ message: string }, [string[]]>(
    (order) => adminAPI.reorderWithdrawalCascade({ order })
  );

  return {
    // Data
    positions: configApi.data?.positions ?? [],
    enabled: configApi.data?.enabled ?? true,
    disabledMethods: disabledApi.data?.methods ?? [],

    // Loading states
    isLoading: configApi.isLoading,
    isLoadingDisabled: disabledApi.isLoading,
    error: configApi.error,

    // Actions
    refetch: configApi.execute,
    fetchDisabledMethods: disabledApi.execute,

    // NOTE: toggleCascade removed - use deposit cascade toggle which affects both cascades

    // Hash-based operations
    swapPositions: swapMutation.mutate,
    isSwapping: swapMutation.isLoading,

    dropMethod: dropMutation.mutate,
    isDropping: dropMutation.isLoading,

    addMethod: addMutation.mutate,
    isAdding: addMutation.isLoading,

    reorderCascade: reorderMutation.mutate,
    isReordering: reorderMutation.isLoading,

    isMutating:
      swapMutation.isLoading ||
      dropMutation.isLoading ||
      addMutation.isLoading ||
      reorderMutation.isLoading,
  };
}

// =============================================================================
// Cascade Stats Hook
// =============================================================================

export interface UseCascadeStatsOptions {
  fetchOnMount?: boolean;
  onSuccess?: (data: CascadeStatsResponse) => void;
  onError?: (error: string) => void;
}

/**
 * Hook for fetching cascade performance statistics
 *
 * @example
 * const { stats, isLoading, fetchStats } = useCascadeStats({ fetchOnMount: true });
 * fetchStats('2024-01-15');
 */
export function useCascadeStats(_options: UseCascadeStatsOptions = {}) {
  // TODO: Implement when backend supports getCascadeStats endpoint
  // For now, return empty state

  return {
    data: null,
    isLoading: false,
    error: null,
    isError: false,
    isSuccess: false,
    stats: [] as CascadeStatsResponse['stats'],
    date: null as string | null,
    fetchStats: async (_date?: string) => null,
    refetch: async () => null,
  };
}
